import { TOOL_KINDS, type ToolKind } from "../toolSelection";

interface Props {
  /** The tools being chosen between -- for the count on each option. */
  tools: { readOnly: boolean }[];
  value: ToolKind;
  disabled?: boolean;
  onChange: (kind: ToolKind) => void;
}

/**
 * Read, write, or all.
 *
 * Its own component because both places that select tools need exactly this control and
 * exactly this vocabulary: the dialog for one account, and the page that spans every
 * account. Two segmented controls that disagreed about what "read" means -- or about
 * whether the counts include tools already exposed -- would be a small lie told twice.
 *
 * The counts are the reason this takes the tools rather than three numbers. They are what
 * makes the control answer a question before it is clicked ("is anything here able to
 * write?"), and computing them beside the filter is what keeps them describing the same
 * list the buttons are about to narrow.
 */
export default function ToolKindFilter({ tools, value, disabled, onChange }: Props) {
  const counts: Record<ToolKind, number> = {
    all: tools.length,
    read: tools.filter((t) => t.readOnly).length,
    write: tools.filter((t) => !t.readOnly).length,
  };

  return (
    <div className="kinds" role="group" aria-label="Filter tools by kind">
      {TOOL_KINDS.map((kind) => (
        <button
          key={kind.id}
          type="button"
          className={`kind ${value === kind.id ? "kind-on" : ""}`}
          aria-pressed={value === kind.id}
          disabled={disabled}
          title={kind.hint}
          onClick={() => onChange(kind.id)}
        >
          {kind.label}
          <span className="kind-count">{counts[kind.id]}</span>
        </button>
      ))}
    </div>
  );
}
